import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Banner, BannerVariant } from 'components/common/banner'
import { useCurrentPractice } from 'contexts/PracticeContext'
import { BusinessVerificationStatus } from 'gql/generated-types/custom-graphql'
import { selectContext } from 'selectors/auth'
import {
	BUSINESS_VERIFICATION_PATH,
	isLendingOnlyPractice,
	shouldShowBusinessVerificationBanner
} from 'util/businessVerification'
import { isManagerOrAdmin } from 'util/unified'

/**
 * Prompts a practice that still owes its EIN verification. Every user sees the banner so the
 * whole clinic knows action is pending, but only managers and admins get the button, since
 * they are the only ones the Settings tab renders for.
 */
export const PracticeBusinessVerificationBanner = () => {
	const context = useSelector(selectContext)
	const { currentPracticeObject, isFinancingEnabled, isPaymentEnabled } = useCurrentPractice()
	const consolidatedPractice = currentPracticeObject?.consolidatedPractice
	const status = consolidatedPractice?.businessVerificationStatus

	const shouldShow = shouldShowBusinessVerificationBanner({
		status,
		promptedAt: consolidatedPractice?.businessVerificationPromptedAt,
		promptOverride: consolidatedPractice?.settings?.businessVerificationPromptOverride,
		isLendingOnly: isLendingOnlyPractice(isFinancingEnabled, isPaymentEnabled)
	})

	const canVerify = useMemo(
		() => isManagerOrAdmin(context, currentPracticeObject),
		[context, currentPracticeObject]
	)

	if (!shouldShow) {
		return null
	}

	const isRejected = status === BusinessVerificationStatus.Failed

	return (
		<Banner
			title={
				isRejected
					? 'Action needed: your business verification was not approved'
					: 'Action needed: verify your business information'
			}
			subtitle={
				isRejected
					? 'Review the reason below and resubmit your legal business name, EIN, and IRS document.'
					: 'Submit your legal business name, EIN, and an IRS document. Verification is required for every practice receiving financing payouts.'
			}
			buttonTitle="Verify now"
			href={BUSINESS_VERIFICATION_PATH}
			variant={BannerVariant.ERROR}
			showButton={canVerify}
			showCloseButton={false}
		/>
	)
}

export default PracticeBusinessVerificationBanner
